import { useCallback, useState } from 'react'

import { Button } from '~/components/Buttons'
import { Badge } from '~/components/UI/Badge'
import { Checkbox, FormGroup, Input, Label, Radio } from '~/components/unform'
import { CheckboxList } from '~/components/unform/CheckboxList'
import Form from '~/components/unform/Form'
import { isReadOnly } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  campaignStrategiesChecks,
  campaignStrategiesOptions
} from '~/modules/retailMedia/constants/campaignStrategies'

type FormEditLinkProps = {
  handleCancel?: () => void
  options: CheckboxOptionProps[]
  initialData: {
    seller_id?: string
    type?: {
      [key: string]: boolean
    }
    brands: string[]
    enable_impression_share?: boolean
    allow_manage_subpublishers?: boolean
  }
  onSubmit: (data: unknown) => void
  fields: LinkConfig[]
  isLoading?: boolean
  isImpressionShareDisabled?: boolean
}

type Brand = { id: string; name: string }

const defaultFields = ['adTypes', 'sellerId', 'brands']

const FormEditLink: React.FC<FormEditLinkProps> = ({
  handleCancel,
  initialData,
  options,
  onSubmit,
  fields = defaultFields,
  isLoading,
  isImpressionShareDisabled
}) => {
  const { t } = useAPPTranslation()

  /**
   * Handle brands
   */

  const [values, setValues] = useState<Brand[]>(
    initialData?.brands?.map((item, index) => ({
      id: item + index,
      name: item
    })) || []
  )

  type HandleChange = { data: OnChangeInputData; id: string }

  const handleChange = ({ data: { value }, id }: HandleChange) => {
    const handleAction = (brands: { id: string; name: string }[]) => {
      const newData = brands?.map(item =>
        item.id !== id ? item : { ...item, name: value }
      )

      return newData
    }

    setValues(handleAction)
  }

  const handleDeleteBrands = (value: Brand) => {
    const handleAction = (brands: Brand[]) => {
      const newData = brands?.filter(item => item.id !== value.id)

      return newData
    }

    setValues(handleAction)
  }

  const handleAddBrands = useCallback(() => {
    setValues(prevState => {
      if (!prevState) return [{ id: 'new0', name: '' }]

      const length = prevState.length
      const id = `new${length}`
      const name = ''
      return [...prevState, { id, name }]
    })
  }, [])

  return (
    <Form onSubmit={onSubmit} initialData={initialData}>
      {fields.includes('adTypes') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label text={t('rm:formEditLink.form.type')} />
          <CheckboxList options={options} name="type" />
        </FormGroup>
      )}

      {fields.includes('sellerId') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label text={t('rm:formEditLink.form.sellerId')} />
          <Input name="seller_id" disabled={isReadOnly()} />
        </FormGroup>
      )}

      {fields.includes('brands') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label
            text={t('rm:formEditLink.form.brands.label')}
            helperText={t('rm:formEditLink.form.brands.helperText')}
          />

          {values?.map((value, index) => (
            <div className="d-flex mb-1 gap-1" key={value.id}>
              <Input
                name={`brands[${index}]`}
                defaultValue={value.name}
                onChange={data => handleChange({ data, id: value.id })}
                disabled={isReadOnly()}
              />

              {!isReadOnly() && (
                <Button
                  text={t('c:actions.remove')}
                  onClick={() => handleDeleteBrands(value)}
                  template="criticalTertiary"
                  size="small"
                  customWidth="auto"
                />
              )}
            </div>
          ))}

          <Button
            text={t('rm:formEditLink.form.brands.addButton')}
            size="xsmall"
            template="secondary"
            customWidth="auto"
            iconLeft="+"
            onClick={handleAddBrands}
          />
        </FormGroup>
      )}

      {fields.includes('manageSubpublishers') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label
            htmlFor="allow_manage_subpublishers"
            text={t('rm:formEditLink.form.manageSubpublishers')}
          />
          <Checkbox
            name="allow_manage_subpublishers"
            label={t('rm:formEditLink.form.allow.label')}
          />
        </FormGroup>
      )}

      {fields.includes('strategies') && (
        <>
          <FormGroup className="border-top pt-3 mt-3 mb-2">
            <Label text={t('rm:formEditLink.form.strategy')} />
            <CheckboxList
              options={campaignStrategiesChecks}
              name="strategies"
            />
          </FormGroup>

          <FormGroup className="mb-4">
            <Label text={t('rm:formEditLink.form.defaultStrategy')} />
            <Radio options={campaignStrategiesOptions} name="defaultStrategy" />
          </FormGroup>
        </>
      )}

      {fields.includes('audience') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label
            icon={<Badge text="beta" size="small" template="warning" />}
            htmlFor="enable_audience_campaign"
            text={t('rm:Audiences')}
          />

          <Checkbox
            name="enable_audience_campaign"
            label={t('rm:formEditLink.form.allow.label')}
          />
        </FormGroup>
      )}

      {fields.includes('creativeStudio') && (
        <FormGroup className="border-top pt-3 mt-3">
          <Label
            icon={<Badge text="beta" size="small" template="warning" />}
            htmlFor="enable_creative_studio"
            text={t('rm:creativeStudio')}
            helperText={t('rm:creativeStudio.helper')}
          />

          <Checkbox
            name="enable_creative_studio"
            label={t('rm:formEditLink.form.allow.label')}
          />
        </FormGroup>
      )}

      {fields.includes('totalBudget') && (
        <FormGroup className="border-top pt-3 my-3">
          <Label
            icon={<Badge text="beta" size="small" template="warning" />}
            htmlFor="allow_total_budget_campaign"
            text={t('rm:totalBudget')}
            helperText={t('rm:totalBudget.helper')}
          />

          <Checkbox
            name="allow_total_budget_campaign"
            label={t('rm:formEditLink.form.allow.label')}
          />
        </FormGroup>
      )}

      {fields.includes('impressionShare') && (
        <FormGroup className="border-top pt-3 my-3">
          <Label
            icon={<Badge text="beta" size="small" template="warning" />}
            htmlFor="enable_impression_share"
            text={t('rm:impressionShare')}
            helperText={t(
              isImpressionShareDisabled
                ? 'rm:impressionShare.helper.disabledByPublisher'
                : 'rm:impressionShare.helper'
            )}
          />
          <Checkbox
            name="enable_impression_share"
            label={t('rm:formEditLink.form.allow.label')}
            disabled={isImpressionShareDisabled}
          />
        </FormGroup>
      )}

      {!isReadOnly() && (
        <FormGroup className="actionButtons border-top py-2">
          {handleCancel && (
            <Button
              text={t('common:actions.cancel')}
              onClick={handleCancel}
              template="transparentCritical"
              size="small"
            />
          )}

          <Button
            type="submit"
            text={t('common:actions.save')}
            textLoading={t('common:actions.saving')}
            size="small"
            template="primary"
            loading={isLoading}
          />
        </FormGroup>
      )}
    </Form>
  )
}

export default FormEditLink
